
import type { ActiveMap } from "../render/mapassets";
import { elem } from "./chrome";

export const MAP_NAME_DELAY_MS = 1_000;
export const MAP_NAME_FADE_MS = 300;
export const MAP_NAME_HOLD_MS = 5_000;
const MAP_NAME_ALPHA = 0.9; // `curAlpha += 0.05` until `> 0.9` stops at 0.9000000000000002 (uimapnameshower.py:262-264)

interface MapTitle {
  readonly label: string;
  readonly image?: string;
}

const MAP_TITLES: Readonly<Record<string, MapTitle>> = {
  metin2_map_a1: { label: "Yongan Area", image: "/ui/mapname/a1.png" },
  // `MAP_A3` (`locale_game.txt:256`) is "Yayang Area", not "Jayang".
  metin2_map_a3: { label: "Yayang Area", image: "/ui/mapname/a3.png" },
  metin2_map_b1: { label: "Joan Area", image: "/ui/mapname/b1.png" },
  metin2_map_b3: { label: "Bokjung Area", image: "/ui/mapname/b3.png" },
  metin2_map_c1: { label: "Pyungmoo Area", image: "/ui/mapname/c1.png" },
  metin2_map_c3: { label: "Bakra Area", image: "/ui/mapname/c3.png" },
  metin2_map_deviltower1: {
    label: "Demon Tower",
    image: "/ui/mapname/devil1_title.png",
  },
  metin2_map_guild_01: {
    label: "Jungrang (Guild Area)",
    image: "/ui/mapname/guild1.png",
  },
  metin2_map_guild_02: {
    label: "Waryong (Guild Area)",
    image: "/ui/mapname/guild2.png",
  },
  metin2_map_guild_03: {
    label: "Imha (Guild Area)",
    image: "/ui/mapname/guild3.png",
  },
  metin2_map_milgyo: {
    label: "Hwang Temple (Temple of Darkness)",
    image: "/ui/mapname/milgyo.png",
  },
  // These three associations are the original client's exact non-Arabic
  // table (`uimapnameshower.py:69-72`); their readable English names are
  // `locale_game.txt:269-273`.
  metin2_map_n_desert_01: {
    label: "Yongbi Desert",
    image: "/ui/mapname/desert1.png",
  },
  metin2_map_n_flame_01: {
    label: "Doyyumhwan",
    image: "/ui/mapname/frame1.png",
  },
  map_n_snowm_01: {
    label: "Mount Sohan",
    image: "/ui/mapname/snow1.png",
  },
  metin2_map_spiderdungeon: {
    label: "Kuahlo Dong (Spider Dungeon)",
    image: "/ui/mapname/spider1.png",
  },
  // Same `spider1.tga` for all three floors (`uimapnameshower.py:99-101`).
  metin2_map_spiderdungeon_02: {
    label: "Kuahlo Dong (Spider Dungeon)",
    image: "/ui/mapname/spider1.png",
  },
  metin2_map_spiderdungeon_03: {
    label: "Kuahlo Dong (Spider Dungeon)",
    image: "/ui/mapname/spider1.png",
  },
  metin2_map_trent: {
    label: "Lungsam (Ghost Forest)",
    image: "/ui/mapname/trent.png",
  },
  metin2_map_skipia_dungeon_01: {
    label: "Heaven's Cave",
    image: "/ui/mapname/skipia.png",
  },
  metin2_map_skipia_dungeon_02: {
    label: "Heaven's Cave",
    image: "/ui/mapname/skipia.png",
  },
};

export interface MapNameClock {
  readonly now: () => number;
  readonly requestFrame: (callback: FrameRequestCallback) => number;
  readonly cancelFrame: (handle: number) => void;
}

export function authoritativeMapName(
  loaded: Pick<ActiveMap, "name">,
  welcomeMap: string,
): string | null {
  if (loaded.name === "" || welcomeMap === "") return null;
  return loaded.name === welcomeMap ? loaded.name : null;
}

export function titleForMap(mapName: string): MapTitle | null {
  return MAP_TITLES[mapName] ?? null;
}

export class MapNameShower {
  private readonly root: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly fallback: HTMLElement;
  private readonly clock: MapNameClock;
  private frame = 0;
  private startedAt = 0;

  constructor(
    container: HTMLElement,
    clock: MapNameClock = {
      now: () => performance.now(),
      requestFrame: (callback) => requestAnimationFrame(callback),
      cancelFrame: (handle) => cancelAnimationFrame(handle),
    },
  ) {
    this.clock = clock;
    this.root = elem(document, "div", "map-name-shower");
    this.root.hidden = true;
    this.root.setAttribute("role", "status");
    this.root.setAttribute("aria-live", "polite");

    this.image = elem(document, "img", "map-name-image");
    this.image.draggable = false;

    this.fallback = elem(document, "div", "map-name-fallback");

    this.root.append(this.image, this.fallback);
    container.append(this.root);
  }

  /** Start (or restart) one announcement. False means the map is not known. */
  show(mapName: string): boolean {
    const title = titleForMap(mapName);
    this.stop();
    if (title === null) return false;

    this.root.dataset.mapName = mapName;
    this.root.setAttribute("aria-label", title.label);
    this.image.hidden = title.image === undefined;
    this.fallback.hidden = title.image !== undefined;
    if (title.image !== undefined) {
      this.image.src = title.image;
      this.image.alt = title.label;
    } else {
      this.image.removeAttribute("src");
      this.image.alt = "";
      this.fallback.textContent = title.label;
    }

    this.startedAt = this.clock.now();
    this.root.hidden = false;
    this.root.style.opacity = "0";
    this.frame = this.clock.requestFrame(this.update);
    return true;
  }

  hide(): void {
    this.stop();
  }

  private readonly update: FrameRequestCallback = () => {
    const elapsed = Math.max(0, this.clock.now() - this.startedAt);
    const fadeInEnd = MAP_NAME_DELAY_MS + MAP_NAME_FADE_MS;
    const holdEnd = fadeInEnd + MAP_NAME_HOLD_MS;
    const fadeOutEnd = holdEnd + MAP_NAME_FADE_MS;

    let alpha: number;
    if (elapsed < MAP_NAME_DELAY_MS) alpha = 0;
    else if (elapsed < fadeInEnd) {
      alpha = ((elapsed - MAP_NAME_DELAY_MS) / MAP_NAME_FADE_MS) * MAP_NAME_ALPHA;
    } else if (elapsed < holdEnd) alpha = MAP_NAME_ALPHA;
    else if (elapsed < fadeOutEnd) {
      alpha = (1 - (elapsed - holdEnd) / MAP_NAME_FADE_MS) * MAP_NAME_ALPHA;
    } else {
      this.root.style.opacity = "0";
      this.root.hidden = true;
      this.frame = 0;
      return;
    }

    this.root.style.opacity = String(alpha);
    this.frame = this.clock.requestFrame(this.update);
  };

  private stop(): void {
    if (this.frame !== 0) this.clock.cancelFrame(this.frame);
    this.frame = 0;
    this.root.hidden = true;
    this.root.style.opacity = "0";
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  get opacity(): number {
    return Number(this.root.style.opacity || 0);
  }
}
